<script lang="ts">
  import { Switch } from 'bits-ui';

  interface Props {
    id?: string;
    checked?: boolean;
    disabled?: boolean;
    label: string;
    onCheckedChange?: (checked: boolean) => void;
  }

  let {
    id,
    checked = $bindable(false),
    disabled = false,
    label,
    onCheckedChange,
  }: Props = $props();
</script>

<Switch.Root {id} bind:checked {disabled} {onCheckedChange} class="switch-root" aria-label={label}>
  <Switch.Thumb class="switch-thumb" />
</Switch.Root>

<style>
  :global(.switch-root) {
    align-items: center;
    background: transparent;
    border: 0;
    border-radius: var(--radii-pill);
    box-shadow: inset 0 0 0 var(--border-width-400) currentcolor;
    box-sizing: border-box;
    cursor: pointer;
    display: inline-flex;
    flex: 0 0 auto;
    height: 1.5rem;
    opacity: var(--opacity-p300);
    padding: 0;
    position: relative;
    user-select: none;
    width: 2.75rem;
  }

  :global(.switch-root::after) {
    content: '';
    inset: calc((var(--target-hit) - 1.5rem) / -2) 0;
    position: absolute;
  }

  :global(.switch-root[data-state='checked']) {
    background: var(--primary-main);
    box-shadow: none;
    color: var(--primary-on-main);
    opacity: var(--opacity-p500);
  }

  :global(.switch-root:focus-visible) {
    outline: var(--focus-ring-width) solid var(--focus-ring);
    outline-offset: var(--focus-ring-offset);
  }

  :global(.switch-root[data-disabled]) {
    cursor: not-allowed;
    opacity: var(--opacity-disabled);
  }

  :global(.switch-thumb) {
    background: currentcolor;
    border-radius: var(--radii-pill);
    display: block;
    height: 0.875rem;
    translate: 0.3125rem 0;
    width: 0.875rem;
  }

  :global(.switch-root[data-state='checked'] .switch-thumb) {
    scale: 1.286;
    translate: 1.4375rem 0;
  }

  @media (prefers-reduced-motion: no-preference) {
    :global(.switch-root) {
      transition:
        background-color var(--motion-normal) var(--motion-easing-standard),
        opacity var(--motion-normal) var(--motion-easing-standard);
    }

    :global(.switch-thumb) {
      transition:
        translate var(--duration-fast) var(--ease-smooth-out),
        scale var(--duration-fast) var(--ease-smooth-out);
    }

    :global(.switch-root:not([data-state='checked']) .switch-thumb) {
      transition:
        translate var(--motion-normal) var(--ease-smooth-out),
        scale var(--motion-normal) var(--ease-smooth-out);
    }
  }
</style>
